import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import sprite from 'assets/svg/match_timeline_iconsprite.svg';
import {
  Team,
  TimelineUpdate,
  TimelineUpdateType,
  ScoreContent,
  CardContent,
  StatusContent,
  EditorSignedContent,
  EditorPhotoContent,
  EditorEmbedContent,
  TeamInfo,
} from '../../../utils/types';

import './styles.scss';

interface Props {
  update: TimelineUpdate;
}

/**
 * CONTENT TYPE & STRUCTURE ASSERTIONS
 */
// TODO: flesh out assertion functions

const isCardContent = (c: any): c is CardContent =>
  c && typeof c !== 'string' && 'player' in c && 'comment' in c;

const isScoreContent = (c: any): c is ScoreContent =>
  c &&
  typeof c !== 'string' &&
  'score' in c &&
  c.score &&
  'home' in c.score &&
  'away' in c.score &&
  'scorer' in c &&
  'type' in c &&
  'comment' in c;

const isStatusContent = (c: any): c is StatusContent =>
  c && typeof c !== 'string' && 'statusType' in c;

const hasScoreInStatus = (c: StatusContent): c is Required<StatusContent> =>
  !!('score' in c && c.score && 'home' in c.score && 'away' in c.score);

/**
 * SIMPLE UPDATE
 */

export const TimelineUpdateSimple: FC<Props> = ({
  update: {type, teamType, minute, stoppageTime, title, content},
}: Props) => {
  const iconId = useMemo(() => {
    switch (type) {
      case TimelineUpdateType.VIDEO_ASSISTANT_REFEREE_OVER:
        return TimelineUpdateType.VIDEO_ASSISTANT_REFEREE;
      case TimelineUpdateType.TIMEOUT_OVER:
      case TimelineUpdateType.TIMEOUT_VOLLEYBALL:
      case TimelineUpdateType.TIMEOUT_HANDBALL:
        return TimelineUpdateType.TIMEOUT;
      case TimelineUpdateType.FOUL:
        return TimelineUpdateType.REBOUND;
      case TimelineUpdateType.SCORE_CHANGE:
        return `${TimelineUpdateType.SCORE_CHANGE}_basketball`; // TODO: fix for score_change_volleyball
      default:
        return type;
    }
  }, [type]);

  const isBoldTitle = useMemo(
    () =>
      [
        TimelineUpdateType.YELLOW_CARD,
        TimelineUpdateType.RED_CARD,
        TimelineUpdateType.YELLOW_RED_CARD,
        TimelineUpdateType.SHOT_ON_TARGET,
        TimelineUpdateType.PENALTY_AWARDED,
        TimelineUpdateType.PENALTY_MISSED,
        TimelineUpdateType.INJURY,
        TimelineUpdateType.INJURY_RETURN,
        TimelineUpdateType.SUBSTITUTION,
        TimelineUpdateType.SCORE_CHANGE,
      ].includes(type),
    [type]
  );

  const isHighlight = useMemo(
    () =>
      [
        TimelineUpdateType.RED_CARD,
        TimelineUpdateType.YELLOW_CARD,
        TimelineUpdateType.YELLOW_RED_CARD,
        TimelineUpdateType.PENALTY_AWARDED,
        TimelineUpdateType.PENALTY_MISSED,
        TimelineUpdateType.INJURY,
        TimelineUpdateType.SUBSTITUTION,
        TimelineUpdateType.VIDEO_ASSISTANT_REFEREE_OVER,
      ].includes(type),
    [type]
  );

  const scorePanel = useMemo(
    () =>
      type === TimelineUpdateType.SCORE_CHANGE &&
      isScoreContent(content) && (
        <div className={`TimelineUpdateSimple__score MiniScore MiniScore--${teamType}`}>
          <span>{content.score.home}</span>:<span>{content.score.away}</span>
        </div>
      ),
    [type, content]
  );

  const playersAnnotation = useMemo(() => {
    if (
      type === TimelineUpdateType.SUBSTITUTION &&
      content &&
      typeof content !== 'string' &&
      'players' in content &&
      content.players
    ) {
      return (
        <>
          <p className="TimelineUpdateSimple__player TimelineUpdateSimple__player--out">
            {content.players.out}
          </p>
          <p className="TimelineUpdateSimple__player TimelineUpdateSimple__player--in">
            {content.players.in}
          </p>
        </>
      );
    }
    if (
      type === TimelineUpdateType.SCORE_CHANGE &&
      isScoreContent(content) &&
      'scorer' in content &&
      content.scorer
    ) {
      return <p className="TimelineUpdateSimple__text">{content.scorer}</p>;
    }
    if (typeof content === 'object' && 'player' in content && content.player) {
      return <p className="TimelineUpdateSimple__text">{content.player}</p>;
    }
    if (content && typeof content === 'string') {
      return <p className="TimelineUpdateSimple__text">{content}</p>;
    }

    return null;
  }, [type, content]);

  const comment = useMemo(
    () =>
      (isScoreContent(content) || isCardContent(content)) &&
      content.comment && (
        <p
          className="TimelineUpdateSimple__comment Comment"
          dangerouslySetInnerHTML={{__html: content.comment}}
        />
      ),
    [content]
  );

  return (
    <article
      className={`TimelineUpdateSimple TimelineUpdateSimple--${teamType} ${
        isHighlight ? 'TimelineUpdateSimple--highlight' : ''
      }`}
    >
      <div className="TimelineUpdateSimple__container">
        <div className="TimelineUpdateSimple__contentWrapper">
          <span className="TimelineUpdateSimple__time">
            {minute && `${minute}'${stoppageTime ? `+${stoppageTime}` : ''}`}
          </span>
          <div className="TimelineUpdateSimple__bullet" />
          {iconId ? (
            <svg
              className={`TimelineUpdateSimple__icon ${
                scorePanel ? 'TimelineUpdateSimple__icon--with-score' : ''
              }`}
            >
              <use href={`${sprite}#${iconId}`} />
            </svg>
          ) : (
            <div className={`TimelineUpdateSimple__icon TimelineUpdateSimple__icon--${type}`} />
          )}
          <div className="TimelineUpdateSimple__content">
            {scorePanel}
            <h1
              className={`TimelineUpdateSimple__title ${
                isBoldTitle ? 'TimelineUpdateSimple__title--bold' : ''
              }`}
            >
              {title}
            </h1>
            {playersAnnotation}
          </div>
        </div>
      </div>
      {comment}
    </article>
  );
};

/**
 * GOAL UPDATE
 */

interface GoalProps extends Props {
  teams: {home: TeamInfo; away: TeamInfo};
}

export const TimelineUpdateGoal: FC<GoalProps> = ({
  update: {teamType, minute, stoppageTime, teamName, title, content},
  teams,
}: GoalProps) => {
  const isOwn = useMemo(() => isScoreContent(content) && content.type === 'own_goal', [content]);

  const scorePanel = useMemo(() => {
    const homeScore = isScoreContent(content) && content.score.home;
    const awayScore = isScoreContent(content) && content.score.away;

    return isScoreContent(content) ? (
      <div className="TimelineUpdateGoal__scorePanel">
        <span
          className={`TimelineUpdateGoal__score ${
            teamType === Team.HOME ? 'TimelineUpdateGoal__score--bold' : ''
          }`}
        >
          {homeScore}
        </span>
        <span className="TimelineUpdateGoal__scoreSeparator">:</span>
        <span
          className={`TimelineUpdateGoal__score ${
            teamType === Team.AWAY ? 'TimelineUpdateGoal__score--bold' : ''
          }`}
        >
          {awayScore}
        </span>
      </div>
    ) : null;
  }, [teamType, content]);

  const heading = useMemo(
    () => (
      <h1 className="TimelineUpdateGoal__title">
        <span>{title}</span>
        {teamType && teamType !== Team.UNDEFINED && teams && teams[teamType] && (
          <span>{teams[teamType].name}</span>
        )}
      </h1>
    ),
    [title, teams, teamType]
  );

  const scorerName = useMemo(
    () =>
      isScoreContent(content) && 'scorer' in content && content.scorer ? (
        <p className="TimelineUpdateGoal__scorer">{content.scorer}</p>
      ) : null,
    [content]
  );

  const comment = useMemo(
    () =>
      isScoreContent(content) &&
      content.comment && (
        <p
          className="TimelineUpdateGoal__comment Comment"
          dangerouslySetInnerHTML={{__html: content.comment}}
        />
      ),
    [content]
  );

  return (
    <article
      className={`TimelineUpdateGoal TimelineUpdateGoal--${teamType} ${
        isOwn ? 'TimelineUpdateGoal--own' : ''
      }`}
    >
      <div className="TimelineUpdateGoal__container">
        <span className="TimelineUpdateGoal__time">
          {minute}&apos;{stoppageTime ? `+${stoppageTime}` : ''}
        </span>
        <div className="TimelineUpdateGoal__bullet" />
        <svg className="TimelineUpdateGoal__icon">
          <use href={`${sprite}#score_change`} />
        </svg>
        <div className="TimelineUpdateGoal__content">
          {scorePanel}
          {heading}
          {scorerName}
        </div>
      </div>
      {comment}
    </article>
  );
};

/**
 * STATUS UPDATE
 */

interface StatusProps extends Props {
  teams: {home: TeamInfo; away: TeamInfo};
}

export const TimelineUpdateStatus: FC<StatusProps> = ({
  update: {type, minute, title, content},
  teams,
}: StatusProps) => {
  const formattedTitle = useMemo(
    () =>
      type === TimelineUpdateType.INJURY_TIME_SHOWN && content ? `${title}: ${content}'` : title,
    [type, title]
  );

  const statusScore = useMemo(() => {
    if (
      [
        TimelineUpdateType.MATCH_ENDED,
        TimelineUpdateType.PERIOD_SCORE,
        TimelineUpdateType.PERIOD_ENDED,
        TimelineUpdateType.BREAK_START,
      ].includes(type) &&
      isStatusContent(content) &&
      hasScoreInStatus(content)
    ) {
      return (
        <div className="TimelineUpdateStatus__scoreContainer">
          <span className="TimelineUpdateStatus__scoreTeam">{teams.home.shortName}</span>
          <span className="TimelineUpdateStatus__scoreResult">
            <span>{content.score.home}</span>-<span>{content.score.away}</span>
          </span>
          <span className="TimelineUpdateStatus__scoreTeam">{teams.away.shortName}</span>
        </div>
      );
    }
    return null;
  }, [content]);

  // exclude first period_start as it coincides with match_started
  return type === TimelineUpdateType.PERIOD_START && minute === '0' ? null : (
    <article
      className={`TimelineUpdateStatus ${
        type !== TimelineUpdateType.INJURY_TIME_SHOWN ? 'TimelineUpdateStatus--highlight' : ''
      }`}
    >
      <div className="TimelineUpdateStatus__header">
        <h1 className="TimelineUpdateStatus__title">{formattedTitle}</h1>
      </div>
      {statusScore}
    </article>
  );
};

/**
 * EDITOR UPDATE
 */

const isEditorSignedContent = (c: any): c is EditorSignedContent =>
  c && typeof c !== 'string' && 'editorImageUrl' in c && 'comment' in c;

const isEditorPhotoContent = (c: any): c is EditorPhotoContent =>
  c && typeof c !== 'string' && 'photoUrl' in c;

const isEditorEmbedContent = (c: any): c is EditorEmbedContent =>
  c && typeof c !== 'string' && 'embed' in c;

export const TimelineUpdateEditor: FC<Props> = ({update}: Props) => {
  const {id, type, title, content} = update;

  const [scripts, setScripts] = useState<HTMLScriptElement[]>([]);
  const scriptContainerRef = useRef<HTMLParagraphElement>(null);
  const optaContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scriptContainerRef.current) {
      const container = scriptContainerRef.current;
      scripts.forEach(scr => container && container.appendChild(scr));

      return () => {
        if (container) {
          scripts.forEach(scr => container && container.removeChild(scr));
        }
      };
    }
    return undefined;
  }, [scripts, scriptContainerRef]);

  // init opta library
  useEffect(() => {
    const container = optaContainerRef.current;

    if (type === TimelineUpdateType.OPTA_DATA && container) {
      if (!window.Opta) {
        window.opta_settings = {
          subscription_id: '099c39bdf153332c75b3be86bc45c112',
          language: 'el_GR',
          timezone: 'Europe/Athens',
        };

        const script = document.createElement('script');
        script.src = 'https://secure.widget.cloud.opta.net/v3/v3.opta-widgets.js';
        script.async = true;
        document.head.appendChild(script);

        const style = document.createElement('link');
        style.rel = 'stylesheet';
        style.href = 'https://secure.widget.cloud.opta.net/v3/css/v3.all.opta-widgets.css';
        document.head.appendChild(style);
      } else {
        window.Opta.start();
      }
    }

    return () => {
      if (type === TimelineUpdateType.OPTA_DATA && container && window.Opta) {
        const optaWidgets = container.querySelectorAll('.Opta');
        optaWidgets.forEach(ow => {
          const widget = window.Opta.widgets[ow.id];
          if (widget) widget.destroy();
        });
      }
    };
  });

  const iconId = useMemo(() => {
    switch (type) {
      case TimelineUpdateType.OPEN_TEXT:
      case TimelineUpdateType.EDITOR_HIGHLIGHT:
      case TimelineUpdateType.VIOLENT_INCIDENT:
        return TimelineUpdateType.EDITOR_INFO;
      case TimelineUpdateType.SCORE_CHANGE:
        return `${TimelineUpdateType.SCORE_CHANGE}`; // TODO: fix for score_change_volleyball
      default:
        return type;
    }
  }, [type]);

  const media = useMemo(() => {
    if (
      [TimelineUpdateType.PHOTO_HIGHLIGHT, TimelineUpdateType.SPONSORED].includes(type) &&
      isEditorPhotoContent(content)
    ) {
      return (
        content.photoUrl && (
          <div className="TimelineUpdateEditor__media">
            <figure>
              <img
                className="TimelineUpdateEditor__photo"
                src={content.photoUrl}
                alt={content.photoCaption ?? 'Φωτογραφία'}
              />
              {content.photoCaption && (
                <p className="TimelineUpdateEditor__caption">{content.photoCaption}</p>
              )}
            </figure>
          </div>
        )
      );
    }

    if (
      [
        TimelineUpdateType.VIDEO_HIGHLIGHT,
        TimelineUpdateType.OPTA_DATA,
        TimelineUpdateType.SPONSORED,
      ].includes(type) &&
      isEditorEmbedContent(content)
    ) {
      return (
        content.embed && (
          <div className="TimelineUpdateEditor__media">
            <div
              className={`TimelineUpdateEditor__embed ${
                type !== TimelineUpdateType.OPTA_DATA
                  ? 'TimelineUpdateEditor__embed--intrinsic'
                  : ''
              }`}
              dangerouslySetInnerHTML={{__html: content.embed}}
              ref={optaContainerRef}
            />
          </div>
        )
      );
    }

    if (type === TimelineUpdateType.EXPERT_COMMENT && isEditorSignedContent(content)) {
      return (
        content.editorImageUrl && (
          <img
            className="TimelineUpdateEditor__editorImage"
            src={content.editorImageUrl}
            alt={title}
          />
        )
      );
    }

    return null;
  }, [type, content]);

  const comment = useMemo(() => {
    if (content && typeof content === 'string') {
      // handle scripts from social embeds
      let html = content;
      const scriptMatches = Array.from(content.matchAll(/<script.*src="([^"]+)".*>.*<\/script>/g));

      scriptMatches.forEach(([script, url]) => {
        html = html.replace(script, '');
        const scriptEl = document.createElement('script');
        scriptEl.src = url;
        scriptEl.async = true;
        setScripts([...scripts, scriptEl]);
      });

      return (
        <p
          className="TimelineUpdateEditor__comment Comment"
          dangerouslySetInnerHTML={{__html: content}}
          ref={scriptContainerRef}
        />
      );
    }

    if (
      [
        TimelineUpdateType.EXPERT_COMMENT,
        TimelineUpdateType.PHOTO_HIGHLIGHT,
        TimelineUpdateType.VIDEO_HIGHLIGHT,
        TimelineUpdateType.OPTA_DATA,
        TimelineUpdateType.SPONSORED,
      ].includes(type) &&
      (isEditorSignedContent(content) ||
        isEditorPhotoContent(content) ||
        isEditorEmbedContent(content))
    )
      return (
        content.comment && (
          <p
            className="TimelineUpdateEditor__comment Comment"
            dangerouslySetInnerHTML={{__html: content.comment}}
          />
        )
      );

    return null;
  }, [type, content]);

  return (
    <article className={`TimelineUpdateEditor TimelineUpdateEditor--${type}`}>
      <div className="TimelineUpdateEditor__header">
        <svg className="TimelineUpdateEditor__icon">
          <use href={`${sprite}#${iconId}`} />
        </svg>
        <h1 className="TimelineUpdateEditor__title">{title}</h1>
      </div>
      {media}
      {comment}
    </article>
  );
};
